import {h, Fragment} from "preact"
import { useState, useEffect, useRef } from "preact/hooks"

let selectIdCounter = 0

function useUniqueId(prefix) {
    const idRef = useRef(null)

    if (!idRef.current) {
        selectIdCounter += 1
        idRef.current = `${prefix}-${selectIdCounter}`
    }

    return idRef.current
}

export default function CustomSelect({ value, onChange, options, label, className }) {
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(() => {
        const selectedIndex = options.findIndex(option => option.value === value)
        return selectedIndex >= 0 ? selectedIndex : 0
    })
    const dropdownRef = useRef(null)
    const buttonRef = useRef(null)
    const optionRefs = useRef([])
    const listboxId = useUniqueId("custom-select-menu")
    const buttonId = useUniqueId("custom-select-button")

    const currentOption = options.find(option => option.value === value) || options[0]

    const openDropdown = () => {
        const selectedIndex = options.findIndex(option => option.value === value)
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
        setIsOpen(true)
    }

    const closeDropdown = (shouldFocusButton = true) => {
        setIsOpen(false)
        if (shouldFocusButton && buttonRef.current) {
            buttonRef.current.focus()
        }
    }

    const handleToggle = () => {
        if (isOpen) {
            closeDropdown()
        } else {
            openDropdown()
        }
    }

    const handleButtonKeyDown = (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault()
            openDropdown()
        } else if (event.key === "Escape" && isOpen) {
            event.preventDefault()
            closeDropdown()
        }
    }

    const handleOptionSelect = (option) => {
        if (onChange) {
            onChange({ target: { value: option.value } })
        }
        closeDropdown()
    }

    const moveActiveIndex = (direction) => {
        setActiveIndex(prevIndex => {
            if (!options.length) {
                return prevIndex
            }

            const nextIndex = (prevIndex + direction + options.length) % options.length
            return nextIndex
        })
    }

    const handleOptionKeyDown = (event, option) => {
        if (event.key === "ArrowDown") {
            event.preventDefault()
            moveActiveIndex(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            moveActiveIndex(-1)
        } else if (event.key === "Home") {
            event.preventDefault()
            setActiveIndex(0)
        } else if (event.key === "End") {
            event.preventDefault()
            setActiveIndex(options.length - 1)
        } else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleOptionSelect(option)
        } else if (event.key === "Escape") {
            event.preventDefault()
            closeDropdown()
        } else if (event.key.length === 1) {
            const searchKey = event.key.toLowerCase()
            const matchIndex = options.findIndex(opt => opt.label.toLowerCase().startsWith(searchKey))
            if (matchIndex >= 0) {
                setActiveIndex(matchIndex)
            }
        }
    }

    useEffect(() => {
        if (isOpen && optionRefs.current[activeIndex]) {
            optionRefs.current[activeIndex].focus()
        }
    }, [isOpen, activeIndex])

    useEffect(() => {
        const selectedIndex = options.findIndex(option => option.value === value)
        const nextIndex = selectedIndex >= 0 ? selectedIndex : 0
        setActiveIndex(prevIndex => prevIndex === nextIndex ? prevIndex : nextIndex)
    }, [value, options])

    // Close dropdown when clicking outside the component
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    return (
        <div className={`ns-custom-select ${className || ''}`} ref={dropdownRef}>
            <button
                type="button"
                id={buttonId}
                ref={buttonRef}
                className={`ns-custom-select-head ${isOpen ? 'is-active' : ''}`}
                onClick={handleToggle}
                onKeyDown={handleButtonKeyDown}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-label={label || "Select"}
            >
                <span className="ns-custom-select-label">{currentOption?.label}</span>
                <span className="ns-custom-select-arrow"></span>
            </button>
            
            <ul 
                id={listboxId}
                role="listbox"
                aria-labelledby={buttonId}
                aria-activedescendant={isOpen && options.length ? `${listboxId}-option-${activeIndex}` : undefined}
                className={`ns-custom-select-list ${isOpen ? 'is-active' : ''}`}
                onClick={(event) => event.stopPropagation()}
                onBlur={(event) => {
                    const nextTarget = event.relatedTarget
                    if (!dropdownRef.current || dropdownRef.current.contains(nextTarget)) {
                        return
                    }
                    closeDropdown(false)
                }}
            >
                {options.map((option, index) => (
                    <li 
                        key={option.value}
                        id={`${listboxId}-option-${index}`}
                        role="option"
                        ref={(element) => {
                            optionRefs.current[index] = element
                        }}
                        tabIndex={index === activeIndex ? 0 : -1}
                        aria-selected={option.value === value}
                        className={`ns-custom-select-item ${option.value === value ? 'is-selected' : ''}`}
                        onClick={() => handleOptionSelect(option)}
                        onKeyDown={(event) => handleOptionKeyDown(event, option)}
                        onMouseEnter={() => setActiveIndex(index)}
                    >
                        {option.label}
                    </li>
                ))}
            </ul>
        </div>
    )
}
